import {
  AlertTriangle,
  CalendarClock,
  KeyRound,
  MapPin,
  UserPlus,
} from 'lucide-react';
import type { ScheduleCoachDashboardAttentionItem } from '@tark/shared/types';

export function formatCodeLabel(value?: string | null, fallback: string = ''): string {
  if (!value) {
    return fallback;
  }

  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatProviderWorkflowState(workflowState?: string | null): string {
  switch (workflowState) {
    case 'DRAFT':
      return 'Draft';
    case 'IN_REVIEW':
      return 'In Review';
    case 'APPROVED':
      return 'Approved';
    case 'LIVE':
      return 'Live';
    case 'PAUSED':
      return 'Paused';
    case 'SUSPENDED':
      return 'Suspended';
    case 'ARCHIVED':
      return 'Archived';
    default:
      return 'Community';
  }
}

export function formatCompactDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Today';
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Now';
  }

  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function formatSessionWindow(startsAt: string, endsAt: string): string {
  const startDate = new Date(startsAt);
  const endDate = new Date(endsAt);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return 'Scheduled';
  }

  return `${formatTime(startsAt)} - ${formatTime(endsAt)}`;
}

export function formatRelativeTime(value?: string | null): string {
  if (!value) {
    return '';
  }

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return '';
  }

  const diffMinutes = Math.max(1, Math.floor((Date.now() - timestamp) / 60000));
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export function severityClasses(severity?: string | null): string {
  switch ((severity || '').toUpperCase()) {
    case 'HIGH':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'MEDIUM':
      return 'border-orange-200 bg-orange-50 text-orange-700';
    default:
      return 'border-primary/20 bg-primary/10 text-primary';
  }
}

export function attentionIcon(item: ScheduleCoachDashboardAttentionItem) {
  switch (item.actionKey) {
    case 'schedule':
      return item.key === 'missing_locations' ? MapPin : CalendarClock;
    case 'access':
      return item.key === 'no_join_code' ? KeyRound : UserPlus;
    default:
      return AlertTriangle;
  }
}

export function isToday(value?: string | null): boolean {
  if (!value) {
    return false;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return false;
  }

  const now = new Date();
  return date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate();
}
